import { Check, MapPin, ExternalLink } from "lucide-react";
import { showroom, brand } from "../data/siteContent";

export default function Showroom() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="showroom"
      data-testid="showroom-section"
      className="bg-brand-charcoal text-white"
    >
      <div className="grid lg:grid-cols-2 min-h-[70vh]">
        <div className="relative min-h-[340px] lg:min-h-full">
          <img
            src={showroom.image}
            alt={showroom.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/30 to-transparent" />
        </div>

        <div className="flex items-center px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="max-w-xl">
            <p className="text-xs tracking-[0.35em] uppercase text-brand-red mb-5 font-semibold">
              <span className="inline-block w-10 h-px bg-brand-red align-middle mr-4" />
              {showroom.eyebrow}
            </p>
            <h2
              data-testid="showroom-title"
              className="font-serif font-light text-4xl md:text-5xl lg:text-6xl text-white leading-[1.1] tracking-tight mb-6"
            >
              {showroom.title}
            </h2>
            <p className="text-base md:text-lg text-white/80 leading-relaxed mb-8">
              {showroom.text}
            </p>

            <ul className="space-y-3 mb-10">
              {showroom.bullets.map((b, i) => (
                <li
                  key={i}
                  data-testid={`showroom-bullet-${i}`}
                  className="flex items-start gap-3 text-sm md:text-base text-white/85"
                >
                  <Check
                    size={18}
                    className="text-brand-red mt-1 shrink-0"
                    strokeWidth={2}
                  />
                  {b}
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollTo("contact")}
                data-testid="showroom-cta"
                className="bg-brand-red hover:bg-brand-redHover text-white px-8 py-4 text-xs font-semibold tracking-widest uppercase transition-colors"
              >
                {showroom.cta}
              </button>
              <a
                href={`tel:${brand.phone}`}
                data-testid="showroom-phone"
                className="border border-white/50 hover:bg-white hover:text-brand-charcoal text-white px-8 py-4 text-xs font-semibold tracking-widest uppercase transition-colors text-center"
              >
                {brand.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Map block */}
      <div className="border-t border-white/10">
        <div className="grid lg:grid-cols-5">
          <div className="lg:col-span-2 px-6 md:px-12 lg:px-16 py-14 flex flex-col justify-center">
            <p className="text-xs tracking-[0.35em] uppercase text-brand-red mb-5 font-semibold">
              <span className="inline-block w-10 h-px bg-brand-red align-middle mr-4" />
              Τοποθεσία
            </p>
            <h3 className="font-serif font-light text-3xl md:text-4xl text-white leading-tight mb-4">
              Μας βρίσκετε στον Μύστακα Ροδόπης
            </h3>
            <p className="text-white/70 leading-relaxed mb-6">
              Το κατάστημα και το showroom μας βρίσκεται στον Μύστακα Ροδόπης. Περάστε
              να δείτε από κοντά υλικά, χρώματα και δείγματα κατασκευών.
            </p>
            <div className="flex items-start gap-3 text-white mb-6">
              <MapPin
                size={20}
                className="text-brand-red mt-1 shrink-0"
                strokeWidth={1.8}
              />
              <div>
                <p className="font-medium text-base">{brand.address}</p>
                <p className="text-sm text-white/60">{brand.area}</p>
              </div>
            </div>
            <a
              href={brand.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="maps-directions"
              className="inline-flex items-center gap-2 self-start border border-white/50 hover:bg-white hover:text-brand-charcoal text-white px-6 py-3 text-xs font-semibold tracking-widest uppercase transition-colors"
            >
              Οδηγίες πλοήγησης
              <ExternalLink size={14} />
            </a>
          </div>

          <div className="lg:col-span-3 min-h-[380px] relative">
            <iframe
              title="Alumak Showroom — Μύστακας Ροδόπης"
              data-testid="showroom-map"
              src={brand.mapsEmbed}
              className="absolute inset-0 w-full h-full border-0 grayscale contrast-110"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </section>
  );
}
